import type { Notification, NotificationAction } from "@/types";

export type ToastType = "success" | "info" | "warning" | "error";

export type ToastPlacement = "top-right" | "top-left" | "bottom-right" | "bottom-left";

export interface ToastItem {
  id: number;
  type: ToastType;
  title: string | null;
  text: string;
  action: NotificationAction | null;
  duration: number | null;
  count: number;
  touchedAt: number;
  restartKey: number;
}

export const MAX_VISIBLE_TOASTS = 3;

export const DUPLICATE_WINDOW_MS = 3000;

export const DEFAULT_PLACEMENT: ToastPlacement = "bottom-right";

export const DEFAULT_PUBLIC_PLACEMENT: ToastPlacement = "top-right";

const AUTO_CLOSE_MS: Record<ToastType, number | null> = {
  success: 5000,
  info: 5000,
  warning: 8000,
  error: 10000,
};

const PLACEMENTS: ToastPlacement[] = ["top-right", "top-left", "bottom-right", "bottom-left"];

export const normalizeToastType = (type: string | undefined): ToastType => {
  if (type === "error" || type === "danger") {
    return "error";
  }

  if (type === "success" || type === "warning") {
    return type;
  }

  return "info";
};

export const normalizePlacement = (placement: string | null | undefined): ToastPlacement =>
  PLACEMENTS.includes(placement as ToastPlacement)
    ? (placement as ToastPlacement)
    : DEFAULT_PLACEMENT;

export const getPlacementParts = (
  placement: ToastPlacement,
): { vertical: "top" | "bottom"; horizontal: "left" | "right" } => {
  const [vertical, horizontal] = placement.split("-") as ["top" | "bottom", "left" | "right"];

  return { vertical, horizontal };
};

export const getToastDuration = (type: ToastType, duration?: number): number | null => {
  if (duration !== undefined) {
    return duration > 0 ? duration : null;
  }

  return AUTO_CLOSE_MS[type];
};

export const toToastAction = (notification: Notification): NotificationAction | null => {
  if (notification.action) {
    return notification.action;
  }

  return notification.link ? { label: "Go", link: notification.link } : null;
};

export const createToastItem = (notification: Notification, now: number): ToastItem => {
  const type = normalizeToastType(notification.type);

  return {
    id: notification.id as number,
    type,
    title: notification.title ?? null,
    text: notification.text,
    action: toToastAction(notification),
    duration: getToastDuration(type, notification.duration),
    count: 1,
    touchedAt: now,
    restartKey: 0,
  };
};

export const findDuplicateToast = (
  toasts: ToastItem[],
  incoming: ToastItem,
  now: number,
): ToastItem | undefined =>
  toasts.find(
    (toast) =>
      toast.type === incoming.type &&
      toast.title === incoming.title &&
      toast.text === incoming.text &&
      now - toast.touchedAt <= DUPLICATE_WINDOW_MS,
  );

export type StackDecision = { action: "add"; evictedId: number | null } | { action: "drop" };

export const decideStackChange = (
  visibleToasts: ToastItem[],
  incoming: ToastItem,
  maxVisible: number = MAX_VISIBLE_TOASTS,
): StackDecision => {
  if (visibleToasts.length < maxVisible) {
    return { action: "add", evictedId: null };
  }

  const evictable = visibleToasts.filter(
    (toast) => incoming.type === "error" || toast.type !== "error",
  );

  if (evictable.length === 0) {
    return { action: "drop" };
  }

  return { action: "add", evictedId: evictable[0].id };
};
